import { X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import apiClient from '../api/client';
import { Button } from './ui/Button';
import { useToast } from './ui/Toast';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { enumLabel } from '../lib/enumLabel';
import { formatDate, formatEUR } from '../lib/format';
import { inputClass } from '../components/ui/Input';

/** Row of GET /orders — everything the picker shows. */
export interface PickableOrder {
  id: string;
  order_number?: string | null;
  status: string;
  total_ttc?: string | number | null;
  created_at?: string | null;
}

/** Modal shell shared by the order picker and the per-page pickers built on it. */
export function PickerModal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[80vh] w-full max-w-2xl flex-col rounded-lg border bg-card shadow-lg">
        <div className="flex items-center justify-between border-b px-4 py-2">
          <h2 className="text-sm font-semibold">{title}</h2>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <X className="h-4 w-4" />
            Close
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-3">{children}</div>
      </div>
    </div>
  );
}

/**
 * Pick an order from the list instead of pasting its UUID.
 *
 * The list is whatever GET /orders returns for the caller — it is already role-scoped
 * server-side (and RLS-scoped for B2B), so this component never filters for entitlement,
 * only for the operator's own search text. `statuses` narrows the offer to the states the
 * calling screen can actually act on (e.g. discounts only apply before preparation).
 */
export function OrderPicker({
  onPick,
  onClose,
  title,
  statuses,
  emptyHint,
}: {
  onPick: (o: PickableOrder) => void;
  onClose: () => void;
  title?: string;
  statuses?: readonly string[];
  emptyHint?: string;
}) {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [rows, setRows] = useState<PickableOrder[] | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    apiClient
      .get<PickableOrder[] | { content: PickableOrder[] }>('/orders')
      .then(({ data }) => setRows(Array.isArray(data) ? data : (data.content ?? [])))
      .catch((err) => {
        showApiError(err);
        setRows([]);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const needle = q.trim().toLowerCase();
  const shown = (rows ?? [])
    .filter((r) => !statuses || statuses.includes(r.status))
    .filter(
      (r) =>
        !needle ||
        (r.order_number ?? '').toLowerCase().includes(needle) ||
        r.id.toLowerCase().includes(needle)
    );

  return (
    <PickerModal title={title ?? t('components.orderPicker.title')} onClose={onClose}>
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t('components.orderPicker.filter')}
        className={inputClass + ' mb-2'}
      />
      {rows === null ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : shown.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {emptyHint ?? t('components.orderPicker.empty')}
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-muted-foreground">
            <tr>
              <th className="text-left py-1">{t('components.orderPicker.number')}</th>
              <th className="text-left">{t('common.status')}</th>
              <th className="text-right">{t('common.totalTTC')}</th>
              <th className="text-right">{t('common.date')}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr
                key={r.id}
                onClick={() => onPick(r)}
                className="border-t cursor-pointer hover:bg-muted/40"
              >
                <td className="py-1 font-mono text-xs">{r.order_number ?? r.id.slice(0, 8)}</td>
                <td>{enumLabel('orderStatus', r.status)}</td>
                <td className="text-right">{formatEUR(r.total_ttc)}</td>
                <td className="text-right text-muted-foreground">{formatDate(r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </PickerModal>
  );
}

/** Short human label for a picked order — "CMD-000123 — CONFIRMED". */
export function orderLabel(o: PickableOrder): string {
  return `${o.order_number ?? o.id.slice(0, 8)} — ${i18n.t(`domain.orderStatus.${o.status}`, { defaultValue: o.status })}`;
}
